/**
 * MentionRichTextEditor — TipTap editor with @-mention support for the
 * BriteHub forum.
 *
 * - All the formatting extensions of RichTextEditor (Bold / Italic / Underline
 *   / Heading / Bullet / Numbered / Quote / Link / Colour / Highlight).
 * - `@` triggers a suggestion popover that searches members via
 *   `/api/users/search`. Selecting inserts an inline `<a>` link that:
 *     • carries data-mention-id + `mention` class for rendering,
 *     • links to `/profile/public/{userId}` so the read side and clicks
 *       both take the reader to the member's public profile without any
 *       special JS.
 * - Emits raw HTML on onChange, same contract as RichTextEditor, so the
 *   server-side and downstream storage are unchanged.
 */
import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import ReactDOM from 'react-dom/client';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';

// Reuse the same Toolbar UI from RichTextEditor.
import { RichTextToolbar } from './RichTextEditor';

const BACKEND_URL = '';

const MentionListPopup = forwardRef(({ items, command }, ref) => {
  const [selected, setSelected] = useState(0);

  useEffect(() => setSelected(0), [items]);

  const pick = (index) => {
    const item = items[index];
    if (item) command({ id: item.user_id, label: item.name });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelected((s) => (s + items.length - 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelected((s) => (s + 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        pick(selected);
        return true;
      }
      return false;
    },
  }));

  if (!items?.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-md shadow-lg py-1 px-2 text-xs text-slate-500" data-testid="mention-suggestion-empty">
        No members match.
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-lg py-1 max-h-64 overflow-auto min-w-[220px] max-w-[min(420px,calc(100vw-64px))]" data-testid="mention-suggestion-list">
      {items.map((item, i) => (
        <button
          key={item.user_id}
          type="button"
          // Prevent editor blur before onClick fires — without this, mouse
          // selection cancels the suggestion and leaves raw text behind.
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={() => setSelected(i)}
          onClick={() => pick(i)}
          data-testid={`mention-item-${item.user_id}`}
          className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
            i === selected ? 'bg-brand-navy/10 text-brand-navy' : 'text-slate-800 hover:bg-slate-50'
          }`}
        >
          <span className="font-medium whitespace-nowrap">{item.name}</span>
          {item.job_title && <span className="text-xs text-slate-500 truncate min-w-0">{item.job_title}</span>}
        </button>
      ))}
    </div>
  );
});

function mentionSuggestion() {
  return {
    items: async ({ query }) => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/members/search?q=${encodeURIComponent(query || '')}`, {
          credentials: 'include',
        });
        if (!res.ok) return [];
        const data = await res.json();
        // Endpoint returns a bare array of {user_id, name, picture, job_title?}
        return Array.isArray(data) ? data.slice(0, 10) : (data.members || data.users || []).slice(0, 10);
      } catch { return []; }
    },
    render: () => {
      let root;
      let container;
      let popup;
      let listRef;

      return {
        onStart: (props) => {
          container = document.createElement('div');
          // Popup lives on <body> so dialog overflow can't clip it; keep it
          // clickable under Radix's body pointer-events lock and stop the
          // pointerdown reaching Radix's outside-click handler.
          container.style.pointerEvents = 'auto';
          container.addEventListener('pointerdown', (e) => e.stopPropagation());
          root = ReactDOM.createRoot(container);
          listRef = React.createRef();
          root.render(<MentionListPopup ref={listRef} items={props.items} command={props.command} />);
          popup = tippy('body', {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: container,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: (props.clientRect?.()?.bottom || 0) > window.innerHeight / 2 ? 'top-start' : 'bottom-start',
            maxWidth: 'calc(100vw - 16px)',
            arrow: false,
            popperOptions: { strategy: 'fixed' },
            theme: 'light-border',
            // Ensure the popup itself is clickable even when Radix mutates
            // ancestor pointer-events.
            zIndex: 100000,
          });
        },
        onUpdate(props) {
          root.render(<MentionListPopup ref={listRef} items={props.items} command={props.command} />);
          popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect });
        },
        onKeyDown(props) {
          if (props.event.key === 'Escape') {
            popup?.[0]?.hide();
            return true;
          }
          return listRef.current?.onKeyDown(props) || false;
        },
        onExit() {
          popup?.[0]?.destroy();
          setTimeout(() => root?.unmount(), 0);
        },
      };
    },
  };
}

// A Link mark that ignores mention anchors — belt-and-braces to complement
// the Mention priority bump. Any `<a data-mention-id>` is left for the
// MentionExtension to parse.
const MentionExtension = Mention.extend({
  // Beat @tiptap/extension-link's default priority (1000) so mention anchors
  // are parsed as Mention nodes on load, not as generic Link marks (which
  // would strip their data-* attributes and break notifications).
  priority: 1001,
  // Explicitly declare the attributes we render so TipTap can round-trip
  // them when a saved HTML value is loaded back into the editor for edit.
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-mention-id') || el.getAttribute('data-id'),
        renderHTML: (attrs) => (attrs.id ? { 'data-mention-id': attrs.id, 'data-id': attrs.id } : {}),
      },
      label: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-label') || (el.textContent || '').replace(/^@/, ''),
        renderHTML: (attrs) => (attrs.label ? { 'data-label': attrs.label } : {}),
      },
    };
  },
  parseHTML() {
    return [
      { tag: 'a[data-mention-id]' },
      { tag: 'a.mention' },
    ];
  },
  // Render mentions as anchor tags so clicks navigate straight to the member
  // profile without needing custom handlers on the read side.
  renderHTML({ node, HTMLAttributes }) {
    const id = node.attrs.id || '';
    const label = node.attrs.label || 'Member';
    return [
      'a',
      {
        ...HTMLAttributes,
        href: `/profile/public/${id}`,
        class: 'mention',
        'data-mention-id': id,
        'data-id': id,
        'data-label': label,
      },
      `@${label}`,
    ];
  },
});

/**
 * Props mirror RichTextEditor. Additional behaviour: pressing @ opens a
 * suggestion popover.
 */
const MentionRichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Share your thoughts, ask a question, or start a discussion…',
  minHeight = '8rem',
  testId = 'mrte',
  disabled = false,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, codeBlock: false, link: false, underline: false }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
      }).extend({
        // Skip mention anchors so they resolve to Mention nodes instead.
        parseHTML() { return [{ tag: 'a[href]:not([data-mention-id])' }]; },
      }),
      Placeholder.configure({ placeholder }),
      MentionExtension.configure({
        HTMLAttributes: { class: 'mention' },
        suggestion: mentionSuggestion(),
      }),
    ],
    editable: !disabled,
    content: value || '',
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      onChange?.(html === '<p></p>' ? '' : html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (incoming !== current && (incoming !== '' || current !== '<p></p>')) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  return (
    <div className="rich-text-editor border border-slate-300 rounded-md bg-white overflow-hidden" data-testid={testId}>
      <RichTextToolbar editor={editor} testIdPrefix={testId} />
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none px-3 py-2 focus:outline-none"
        style={{ minHeight }}
        data-testid={`${testId}-body`}
      />
    </div>
  );
};

export default MentionRichTextEditor;
